import _ from 'lodash';

import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import {
  BillingParams,
  BillingResponse,
  BillingTypeResponse,
  ConfirmPaymentPayload,
  ConfirmPaymentResponse,
  TableauBillingResponse,
  CreatePaymentIntentResponse,
  InvoiceParams,
} from '@scale/shared/types/endpoints/customer/user/billing';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  ContractReport,
  ContractsParams,
} from '@scale/shared/types/endpoints/customer/user/contracts';
import { Payment, PaymentMethod } from '@scale/shared/types/endpoints/customer/user/payments';

export async function fetchPayments(): Promise<Payment[]> {
  try {
    return apiFetch(null, 'internal/user/payments');
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return [];
  }
}

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  try {
    return apiFetch(null, `internal/user/paymentMethods`);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return [];
}

export async function reportBlockedUserAddedPayment() {
  try {
    return apiFetch(null, `internal/user/reportBlockedUserAddedPayment`);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return [];
}

export async function hasPaymentMethods(): Promise<{
  hasPaymentMethods: boolean;
  isExpired?: boolean;
}> {
  try {
    return apiFetch(null, `internal/user/hasPaymentMethods`);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return { hasPaymentMethods: false };
}

export async function setDefaultPaymentMethods(id: string) {
  try {
    const opts = { method: 'POST', body: { paymentMethodId: id } };
    return apiFetch(null, `internal/user/paymentMethods/default`, opts);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return [];
}

export async function getCardInfo(stripeCustomerId: string | null | undefined) {
  if (stripeCustomerId === 'invoice' || IS_FEDERAL) return null;

  if (!stripeCustomerId) {
    return 'NONE';
  }

  try {
    return apiFetch(null, `internal/user/paymentMethods/default`);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return null;
}

export async function deletePaymentMethod(id: string) {
  try {
    const opts = { method: 'DELETE' };
    return apiFetch(null, `internal/user/paymentMethods/${id}`, opts);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return null;
}

export async function fetchAmountOwed({
  startDate,
  endDate,
}: BillingParams): Promise<BillingResponse> {
  const params = {
    startDate,
    endDate,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/billing', {
    query,
  });
}

export async function loadOldConsumptionCsv({
  startDate,
  endDate,
}: BillingParams): Promise<string> {
  const params = {
    startDate,
    endDate,
  };
  const payload = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/load_old_billing_csv', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function pingOldConsumptionCsv({
  startDate,
  endDate,
}: BillingParams): Promise<boolean> {
  const params = {
    startDate,
    endDate,
    ping: true,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/fetch_old_billing_csv', {
    query,
  });
}

export async function fetchOldConsumptionCsv({
  startDate,
  endDate,
}: BillingParams): Promise<string> {
  const params = {
    startDate,
    endDate,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/fetch_old_billing_csv', {
    query,
  });
}

export async function fetchConsumptionCsv({ startDate, endDate }: BillingParams): Promise<string> {
  const params = {
    startDate,
    endDate,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/billing_csv', {
    query,
  });
}

export async function createInvoicePdf({
  amount,
  startDate,
  invoiceDate,
}: InvoiceParams): Promise<string> {
  const params = {
    amount,
    startDate,
    invoiceDate,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/invoice_pdf', {
    query,
  });
}

export async function createPaymentIntent(): Promise<CreatePaymentIntentResponse> {
  return apiFetch(null, 'internal/user/update_payment/intent', {
    method: 'GET',
  });
}

export async function confirmPayment(
  payload: ConfirmPaymentPayload,
): Promise<ConfirmPaymentResponse> {
  return apiFetch(null, 'internal/user/update_payment/confirm', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

// purchase studio bundle
export async function purchaseStudioSubscription(params: $FIXME) {
  const body = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/studio/purchase_studio_subscription', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getLabelingUnitHistory() {
  return apiFetch(null, 'internal/user/studio/bundle_purchase_history');
}

export async function getCreditsAndDiscounts() {
  return apiFetch(null, 'internal/user/paymentMethods/creditsAndDiscounts');
}

export async function getSubcontracts() {
  return apiFetch(null, 'internal/user/paymentMethods/subcontracts');
}

export async function redeemCode(code: string) {
  try {
    const args = { method: 'POST', body: { code } };
    return apiFetch(null, `internal/user/paymentMethods/redeemCode`, args);
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
  }

  return [];
}

export async function fetchActiveContracts({
  project,
}: ContractsParams): Promise<ContractReport[]> {
  const params = {
    project,
  };
  const query = _.omitBy(params, _.isNil);
  return apiFetch(null, 'internal/user/contracts', { query });
}

export async function getBillingType(): Promise<BillingTypeResponse> {
  return apiFetch(null, 'internal/billing/type');
}

export async function getTableauBilling(): Promise<TableauBillingResponse> {
  return apiFetch(null, 'internal/billing/tableau');
}
